import type { Coordinate, EditableFloorPlan, MultiPolygonCoordinates } from '../models/editor-feature';

/**
 * Maps a floorplan's own drawing coordinates onto the SVG surface.
 *
 * Same approach the booking viewer already uses: fit the whole plan into the viewport
 * preserving aspect ratio, and flip Y, because SVG's Y axis points down while these
 * drawings' Y axis points up. Everything here is plain planar arithmetic — the coordinates
 * are arbitrary local units with no projection and no real-world scale, so anything
 * geographic would be actively wrong.
 */

export interface BoundingBox {
  readonly minX: number;
  readonly minY: number;
  readonly maxX: number;
  readonly maxY: number;
}

export interface Projection {
  readonly scale: number;
  readonly offsetX: number;
  readonly offsetY: number;
  readonly height: number;
  readonly boundingBox: BoundingBox;
}

export const PADDING = 16;

const EMPTY_BOUNDING_BOX: BoundingBox = { minX: 0, minY: 0, maxX: 1, maxY: 1 };

export const computeBoundingBox = (floorPlan: EditableFloorPlan): BoundingBox => {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  const include = ([x, y]: Coordinate): void => {
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  };

  const includePolygons = (coordinates: MultiPolygonCoordinates): void => {
    coordinates.forEach((polygon) => polygon.forEach((ring) => ring.forEach(include)));
  };

  floorPlan.border.forEach((feature) => includePolygons(feature.coordinates));
  floorPlan.floorBackdrop.forEach((feature) => includePolygons(feature.coordinates));
  floorPlan.rooms.forEach((feature) => includePolygons(feature.coordinates));
  floorPlan.deskShapes.forEach((feature) => includePolygons(feature.coordinates));
  floorPlan.desks.forEach((desk) => include(desk.coordinates));
  floorPlan.labels.forEach((label) => include(label.coordinates));

  if (!Number.isFinite(minX) || !Number.isFinite(minY)) {
    return EMPTY_BOUNDING_BOX;
  }

  // A plan can legitimately be a single point or a perfectly straight line; without this
  // the scale below divides by zero and every coordinate becomes NaN.
  return {
    minX,
    minY,
    maxX: maxX > minX ? maxX : minX + 1,
    maxY: maxY > minY ? maxY : minY + 1,
  };
};

export const createProjection = (boundingBox: BoundingBox, width: number, height: number): Projection => {
  const dataWidth = boundingBox.maxX - boundingBox.minX;
  const dataHeight = boundingBox.maxY - boundingBox.minY;

  const scale = Math.min((width - 2 * PADDING) / dataWidth, (height - 2 * PADDING) / dataHeight);

  return {
    scale,
    offsetX: PADDING + (width - 2 * PADDING - dataWidth * scale) / 2,
    offsetY: PADDING + (height - 2 * PADDING - dataHeight * scale) / 2,
    height,
    boundingBox,
  };
};

export const toScreen = (coordinate: Coordinate, projection: Projection): { x: number; y: number } => {
  const [x, y] = coordinate;
  return {
    x: (x - projection.boundingBox.minX) * projection.scale + projection.offsetX,
    y: projection.height - ((y - projection.boundingBox.minY) * projection.scale + projection.offsetY),
  };
};

/** The inverse of {@link toScreen}: what drawing coordinate a point on the surface refers to. */
export const toWorld = (point: { x: number; y: number }, projection: Projection): Coordinate => [
  (point.x - projection.offsetX) / projection.scale + projection.boundingBox.minX,
  (projection.height - point.y - projection.offsetY) / projection.scale + projection.boundingBox.minY,
];

export const multiPolygonToPath = (coordinates: MultiPolygonCoordinates, projection: Projection): string => {
  let path = '';

  coordinates.forEach((polygon) => {
    polygon.forEach((ring) => {
      ring.forEach((coordinate, index) => {
        const { x, y } = toScreen(coordinate, projection);
        path += index === 0 ? `M ${x} ${y} ` : `L ${x} ${y} `;
      });
      path += 'Z ';
    });
  });

  return path;
};

/**
 * Where the plan itself sits on the surface, in surface units.
 *
 * The surface is a fixed 1200×800 space and the plan is fitted into it, so on any plan whose
 * shape is not 3:2 there is empty surface down one pair of edges. Framing the *surface* in a
 * window therefore leaves two margins: one from this fit and another from the window's own
 * aspect, which is what made the drawing look small in a large panel. Framing this instead
 * leaves only the second, and matching the window's aspect removes that too.
 */
export const projectedExtentOf = (projection: Projection): {
  x: number;
  y: number;
  width: number;
  height: number;
} => {
  const { boundingBox } = projection;
  const topLeft = toScreen([boundingBox.minX, boundingBox.maxY], projection);
  const bottomRight = toScreen([boundingBox.maxX, boundingBox.minY], projection);

  return {
    x: Math.min(topLeft.x, bottomRight.x),
    y: Math.min(topLeft.y, bottomRight.y),
    width: Math.abs(bottomRight.x - topLeft.x),
    height: Math.abs(bottomRight.y - topLeft.y),
  };
};
